// @ts-nocheck
import React, { useState, useRef } from 'react';
import { useAdminIssues, useIssueComments, AdminIssue, ISSUE_CATEGORIES } from '@/hooks/useAdminIssues';
import { useAdminList } from '@/hooks/useAdminList';
import { useIssueAttachments } from '@/hooks/useIssueAttachments';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import WysiwygEditor from '@/components/ui/wysiwyg-editor';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Plus, 
  MessageSquare, 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  XCircle, 
  Send,
  Loader2,
  Trash2,
  X,
  GripVertical,
  Tag,
  ChevronDown,
  ChevronUp,
  User,
  Paperclip,
  FileText,
  File,
  Download,
  Upload
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';

const statusConfig = {
  open: { label: 'Open', color: 'bg-blue-500', icon: AlertCircle },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-green-500', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-500', icon: XCircle },
};

const priorityConfig = {
  low: { label: 'Low', color: 'bg-gray-400' },
  medium: { label: 'Medium', color: 'bg-blue-400' },
  high: { label: 'High', color: 'bg-orange-500' },
  urgent: { label: 'Urgent', color: 'bg-red-500' },
};

const getInitial = (p?: { name?: string | null; email?: string | null }) => {
  return (
    p?.name?.trim()?.[0]?.toUpperCase() ||
    p?.email?.trim()?.[0]?.toUpperCase() ||
    'U'
  );
};

// Colors for different admin avatars based on user id
const avatarColors = [
  'bg-blue-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-rose-500',
];

const getAvatarColor = (userId?: string | null) => {
  if (!userId) return 'bg-muted';
  // Generate a consistent color based on user id
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i);
    hash = hash & hash;
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

const EXCERPT_LENGTH = 150;

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

function ExpandableComment({ 
  comment, 
  currentUserId, 
  onDelete 
}: { 
  comment: { id: string; content: string; created_at: string; user_id: string; user?: { name?: string | null; email?: string | null; avatar_url?: string | null } };
  currentUserId?: string;
  onDelete: (id: string) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const plainText = stripHtml(comment.content);
  const isLong = plainText.length > EXCERPT_LENGTH;
  
  return (
    <div className="flex gap-3 p-3 rounded-lg bg-muted/20 border border-border/50">
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarImage src={comment.user?.avatar_url || ''} />
        <AvatarFallback className="text-xs">
          {getInitial(comment.user)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">
            {comment.user?.name || comment.user?.email || 'Unknown'}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
          </span>
          {comment.user_id === currentUserId && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-destructive ml-auto"
              onClick={() => onDelete(comment.id)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
        {isLong && !isExpanded ? (
          <div className="text-sm mt-1">
            <span className="text-muted-foreground">{plainText.slice(0, EXCERPT_LENGTH)}...</span>
            <Button 
              variant="link" 
              size="sm" 
              className="h-auto p-0 ml-1 text-primary"
              onClick={() => setIsExpanded(true)}
            >
              Show more <ChevronDown className="h-3 w-3 ml-0.5" />
            </Button>
          </div>
        ) : (
          <>
            <div 
              className="text-sm mt-1 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: comment.content }}
            />
            {isLong && (
              <Button 
                variant="link" 
                size="sm" 
                className="h-auto p-0 text-primary"
                onClick={() => setIsExpanded(false)}
              >
                Show less <ChevronUp className="h-3 w-3 ml-0.5" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InlineCreateIssue({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const { createIssue, isCreating } = useAdminIssues();
  const { data: admins = [] } = useAdminList();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [category, setCategory] = useState<string>('');
  const [assignedTo, setAssignedTo] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    await createIssue({
      title: title.trim(),
      description: description || undefined,
      priority,
      category: category || undefined,
      assigned_to: assignedTo || undefined,
    });

    setTitle('');
    setDescription('');
    setPriority('medium');
    setCategory('');
    setAssignedTo('');
    onCreated();
    onCancel();
  };

  return (
    <Card className="border-primary/50 shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">New Issue</CardTitle>
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief description of the issue"
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <WysiwygEditor
              value={description}
              onChange={setDescription}
              placeholder="Detailed description with images..."
              className="min-h-[200px]"
            />
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as typeof priority)}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-gray-400" />
                      Low
                    </div>
                  </SelectItem>
                  <SelectItem value="medium">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-blue-400" />
                      Medium
                    </div>
                  </SelectItem>
                  <SelectItem value="high">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-orange-500" />
                      High
                    </div>
                  </SelectItem>
                  <SelectItem value="urgent">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-red-500" />
                      Urgent
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {ISSUE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assign To</Label>
              <Select
                value={assignedTo || undefined}
                onValueChange={(v) => setAssignedTo(v === 'unassigned' ? '' : v)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Unassigned">
                    {assignedTo && (
                      <div className="flex items-center gap-2">
                        <User className="h-3 w-3" />
                        {admins.find(a => a.id === assignedTo)?.name || admins.find(a => a.id === assignedTo)?.email || 'Unknown'}
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">
                    <span className="text-muted-foreground">Unassigned</span>
                  </SelectItem>
                  {admins.map((admin) => (
                    <SelectItem key={admin.id} value={admin.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-5 w-5">
                          <AvatarImage src={admin.avatar_url || ''} />
                          <AvatarFallback className="text-xs">
                            {admin.name?.[0]?.toUpperCase() || admin.email?.[0]?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        {admin.name || admin.email}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1" />
            <div className="flex gap-2 self-end">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating || !title.trim()}>
                {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Issue
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}


function SortableIssueCard({ issue, isSelected, onClick }: { 
  issue: AdminIssue; 
  isSelected: boolean;
  onClick: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: issue.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const StatusIcon = statusConfig[issue.status].icon;

  return (
    <div ref={setNodeRef} style={style}>
      <Card 
        className={`cursor-pointer transition-all hover:shadow-md ${
          isSelected ? 'ring-2 ring-primary border-primary' : ''
        } ${isDragging ? 'shadow-lg' : ''} ${
          issue.status === 'resolved' || issue.status === 'closed' 
            ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800/50 opacity-70' 
            : ''
        }`}
        onClick={onClick}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <div 
              className="cursor-grab active:cursor-grabbing mt-1 text-muted-foreground hover:text-foreground"
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge className={`${priorityConfig[issue.priority].color} text-white text-xs`}>
                  {priorityConfig[issue.priority].label}
                </Badge>
                {issue.category && (
                  <Badge variant="outline" className="text-xs gap-1">
                    <Tag className="h-3 w-3" />
                    {issue.category}
                  </Badge>
                )}
              </div>
              <h4 className="font-medium text-sm line-clamp-2">{issue.title}</h4>
              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <StatusIcon className="h-3 w-3" />
                  {statusConfig[issue.status].label}
                </div>
                <div className="flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />
                  {issue.comment_count || 0}
                </div>
                {issue.assignee && (
                  <div className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {issue.assignee.name || issue.assignee.email || 'Assigned'}
                  </div>
                )}
                <span>{formatDistanceToNow(new Date(issue.created_at), { addSuffix: true })}</span>
              </div>
            </div>
            {issue.assignee ? (
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarImage src={issue.assignee?.avatar_url || ''} />
                <AvatarFallback className={`text-xs text-white ${getAvatarColor(issue.assigned_to)}`}>
                  {getInitial(issue.assignee)}
                </AvatarFallback>
              </Avatar>
            ) : (
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarFallback className="text-xs bg-muted text-muted-foreground">
                  —
                </AvatarFallback>
              </Avatar>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function IssueDetail({ issue }: { issue: AdminIssue }) {
  const { user } = useAuth();
  const { updateIssue, deleteIssue, isUpdating } = useAdminIssues();
  const { data: admins = [] } = useAdminList();
  const { comments, isLoading: commentsLoading, isError: commentsError, error: commentsErrorObj, refetch: refetchComments, addComment, deleteComment, isAddingComment } = useIssueComments(issue.id, issue.title);
  const { attachments, isLoading: attachmentsLoading, isUploading, uploadAttachment, deleteAttachment, isDeleting, getPublicUrl } = useIssueAttachments(issue.id);
  const [newComment, setNewComment] = useState('');
  const [showCommentEditor, setShowCommentEditor] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadAttachment(files[0]);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileIcon = (mimeType: string | null) => {
    if (mimeType?.includes('pdf')) return <FileText className="h-4 w-4 text-red-500" />;
    if (mimeType?.includes('word') || mimeType?.includes('document')) return <FileText className="h-4 w-4 text-blue-500" />;
    if (mimeType?.includes('image')) return <File className="h-4 w-4 text-green-500" />;
    return <File className="h-4 w-4 text-muted-foreground" />;
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleStatusChange = async (status: AdminIssue['status']) => {
    await updateIssue({ id: issue.id, status });
  };

  const handlePriorityChange = async (priority: AdminIssue['priority']) => {
    await updateIssue({ id: issue.id, priority });
  };

  const handleCategoryChange = async (category: string) => {
    await updateIssue({ id: issue.id, category });
  };

  const handleAssigneeChange = async (assigneeId: string) => {
    await updateIssue({
      id: issue.id,
      assigned_to: assigneeId || null,
      previous_assigned_to: issue.assigned_to,
    });
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    await addComment(newComment.trim());
    setNewComment('');
    setShowCommentEditor(false);
  };

  const StatusIcon = statusConfig[issue.status].icon;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="p-4 border-b bg-muted/30">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className={`${priorityConfig[issue.priority].color} text-white`}>
                {priorityConfig[issue.priority].label}
              </Badge>
              {issue.category && (
                <Badge variant="outline" className="gap-1">
                  <Tag className="h-3 w-3" />
                  {issue.category}
                </Badge>
              )}
            </div>
            <h2 className="text-lg font-semibold">{issue.title}</h2>
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
              <Avatar className="h-5 w-5">
                <AvatarImage src={issue.creator?.avatar_url || ''} />
                <AvatarFallback className="text-xs">
                  {getInitial(issue.creator)}
                </AvatarFallback>
              </Avatar>
              <span>
                {issue.creator?.name || issue.creator?.email || 'Unknown'}
              </span>
              <span>•</span>
              <span>{format(new Date(issue.created_at), 'MMM d, yyyy h:mm a')}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select 
              value={issue.status} 
              onValueChange={(v) => handleStatusChange(v as AdminIssue['status'])}
              disabled={isUpdating}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Open
                  </div>
                </SelectItem>
                <SelectItem value="in_progress">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    In Progress
                  </div>
                </SelectItem>
                <SelectItem value="resolved">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Resolved
                  </div>
                </SelectItem>
                <SelectItem value="closed">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4" />
                    Closed
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <Select 
              value={issue.priority} 
              onValueChange={(v) => handlePriorityChange(v as AdminIssue['priority'])}
              disabled={isUpdating}
            >
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-gray-400" />
                    Low
                  </div>
                </SelectItem>
                <SelectItem value="medium">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-blue-400" />
                    Medium
                  </div>
                </SelectItem>
                <SelectItem value="high">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-orange-500" />
                    High
                  </div>
                </SelectItem>
                <SelectItem value="urgent">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-red-500" />
                    Urgent
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <Select 
              value={issue.category || ''} 
              onValueChange={handleCategoryChange}
              disabled={isUpdating}
            >
              <SelectTrigger className="w-[140px]">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  <SelectValue placeholder="Category" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {ISSUE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select 
              value={issue.assigned_to || undefined} 
              onValueChange={(v) => handleAssigneeChange(v === 'unassigned' ? '' : v)}
              disabled={isUpdating}
            >
              <SelectTrigger className="w-[160px]">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <SelectValue placeholder="Assign To" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">
                  <span className="text-muted-foreground">Unassigned</span>
                </SelectItem>
                {admins.map((admin) => (
                  <SelectItem key={admin.id} value={admin.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-5 w-5">
                        <AvatarImage src={admin.avatar_url || ''} />
                        <AvatarFallback className="text-xs">
                          {admin.name?.[0]?.toUpperCase() || admin.email?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      {admin.name || admin.email}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => deleteIssue(issue.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {issue.description && (
          <div 
            className="mt-3 text-sm text-muted-foreground prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: issue.description }}
          />
        )}
      </div>

      {/* Attachments Section */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <Paperclip className="h-4 w-4" />
            Attachments ({attachments.length})
          </h3>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-1" />
              )}
              Upload File
            </Button>
          </div>
        </div>
        
        {attachmentsLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : attachments.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground text-sm">
            <Paperclip className="h-6 w-6 mx-auto mb-1 opacity-50" />
            <p>No attachments</p>
          </div>
        ) : (
          <div className="space-y-2">
            {attachments.map((attachment) => (
              <div 
                key={attachment.id}
                className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/50 hover:bg-muted/50 transition-colors"
              >
                <a
                  href={getPublicUrl(attachment.file_path)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 flex-1 min-w-0 hover:text-primary transition-colors"
                >
                  {getFileIcon(attachment.mime_type)}
                  <span className="text-sm font-medium truncate">{attachment.file_name}</span>
                  {attachment.file_size && (
                    <span className="text-xs text-muted-foreground">
                      ({formatFileSize(attachment.file_size)})
                    </span>
                  )}
                </a>
                <div className="flex items-center gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    asChild
                  >
                    <a
                      href={getPublicUrl(attachment.file_path)}
                      download={attachment.file_name}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => deleteAttachment(attachment)}
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Comments Thread */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Comments ({comments.length})
          </h3>
          {!showCommentEditor && (
            <Button variant="outline" size="sm" onClick={() => setShowCommentEditor(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Comment
            </Button>
          )}
        </div>

        {/* Comment Editor (shown only when adding) */}
        {showCommentEditor && (
          <div className="mb-4 p-3 border rounded-lg bg-muted/30">
            <WysiwygEditor
              value={newComment}
              onChange={setNewComment}
              placeholder="Write a comment..."
              className="min-h-[80px]"
            />
            <div className="flex items-center justify-end gap-2 mt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowCommentEditor(false);
                  setNewComment('');
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddComment} 
                disabled={isAddingComment || !newComment.trim()}
                size="sm"
              >
                {isAddingComment ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Send className="h-4 w-4 mr-1" />
                )}
                Send
              </Button>
            </div>
          </div>
        )}

        {/* Comments List */}
        <div className="space-y-3">
          {commentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : commentsError ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Couldn't load comments</p>
              <p className="text-xs mt-1">{String((commentsErrorObj as any)?.message || '')}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => refetchComments()}>
                Retry
              </Button>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No comments yet</p>
              <p className="text-sm">Be the first to comment</p>
            </div>
          ) : (
            comments.map((comment) => (
              <ExpandableComment 
                key={comment.id} 
                comment={comment} 
                currentUserId={user?.id}
                onDelete={deleteComment}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function IssueTracker() {
  const { issues, isLoading, reorderIssues } = useAdminIssues();
  const { data: admins = [] } = useAdminList();
  const { user } = useAuth();
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const filteredIssues = issues
    .filter(issue => {
      if (statusFilter !== 'all' && issue.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && issue.priority !== priorityFilter) return false;
      if (categoryFilter !== 'all' && issue.category !== categoryFilter) return false;
      if (assigneeFilter !== 'all') {
        if (assigneeFilter === 'me' && issue.assigned_to !== user?.id) return false;
        if (assigneeFilter === 'unassigned' && issue.assigned_to) return false;
        if (assigneeFilter !== 'me' && assigneeFilter !== 'unassigned' && issue.assigned_to !== assigneeFilter) return false;
      }
      return true;
    })
    .sort((a, b) => {
      // Move resolved/closed issues to the bottom
      const aResolved = a.status === 'resolved' || a.status === 'closed';
      const bResolved = b.status === 'resolved' || b.status === 'closed';
      if (aResolved && !bResolved) return 1;
      if (!aResolved && bResolved) return -1;
      return 0; // Preserve existing order within same group
    });

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = filteredIssues.findIndex(i => i.id === active.id);
      const newIndex = filteredIssues.findIndex(i => i.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(filteredIssues, oldIndex, newIndex);
        await reorderIssues(newOrder.map(i => i.id));
      }
    }
  };

  const selectedIssue = issues.find(i => i.id === selectedIssueId);

  const openCount = issues.filter(i => i.status === 'open').length;
  const inProgressCount = issues.filter(i => i.status === 'in_progress').length;

  return (
    <div className="h-[calc(100vh-180px)]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Issue Tracker</h1>
          <p className="text-sm text-muted-foreground">
            {openCount} open, {inProgressCount} in progress • Drag to reorder
          </p>
        </div>
        {!showCreateForm && (
          <Button onClick={() => setShowCreateForm(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New Issue
          </Button>
        )}
      </div>

      {showCreateForm && (
        <div className="mb-4">
          <InlineCreateIssue 
            onCancel={() => setShowCreateForm(false)} 
            onCreated={() => setShowCreateForm(false)} 
          />
        </div>
      )}

      <div className="h-[calc(100%-60px)]">
        <Card className="flex flex-col overflow-hidden h-full">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {ISSUE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Assignee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assignees</SelectItem>
                  <SelectItem value="me">Assigned to Me</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {admins.map((admin) => (
                    <SelectItem key={admin.id} value={admin.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-5 w-5">
                          <AvatarImage src={admin.avatar_url || ''} />
                          <AvatarFallback className="text-xs">
                            {admin.name?.[0]?.toUpperCase() || admin.email?.[0]?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        {admin.name || admin.email}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <ScrollArea className="flex-1">
            <CardContent className="space-y-2 pt-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredIssues.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No issues found</p>
                </div>
              ) : (
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={filteredIssues.map(i => i.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredIssues.map(issue => (
                      <div key={issue.id} className="space-y-2">
                        <SortableIssueCard
                          issue={issue}
                          isSelected={issue.id === selectedIssueId}
                          onClick={() => setSelectedIssueId(selectedIssueId === issue.id ? null : issue.id)}
                        />
                        {selectedIssueId === issue.id && (
                          <Card className="ml-6 border-l-4 border-l-primary">
                            <IssueDetail issue={issue} />
                          </Card>
                        )}
                      </div>
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
}
